import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errMsg } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Gift, AlertTriangle, Loader2, ArrowRight } from 'lucide-react';

const PLAN_LABEL = { free: 'Free', try: 'Try', starter: 'Starter', growth: 'Growth', enterprise: 'Enterprise' };

// Statuses that mean a (trial or paid) subscription failed to convert / lapsed.
const LAPSED = ['failed', 'on_hold', 'expired'];

function daysLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / 86400000));
}

/**
 * Shows one of three states based on /billing/me:
 *  - Active free trial  → amber countdown banner with "Manage plan"
 *  - Lapsed trial/sub   → red "trial ended" banner with one-tap Resubscribe
 *  - Otherwise          → nothing
 */
export const TrialBanner = () => {
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get('/billing/me');
        if (alive) setBilling(r.data);
      } catch (_e) { /* silent — banner is non-critical */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!billing) return null;

  const plan = billing.plan || 'free';
  const status = String(billing.status || 'active').toLowerCase();
  const planLabel = PLAN_LABEL[plan] || plan;
  const price = billing?.plan_details?.price_usd;

  const resubscribe = async () => {
    if (plan === 'free') { navigate('/app/billing/upgrade'); return; }
    setBusy(true);
    try {
      const r = await api.post('/billing/checkout', { plan });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error('Could not start checkout. Please try again.');
      }
    } catch (err) {
      if (err?.response?.status !== 402) toast.error(errMsg(err, 'Could not start checkout'));
    } finally {
      setBusy(false);
    }
  };

  // ---- State 1: lapsed trial / failed conversion → resubscribe ----
  if (plan !== 'free' && LAPSED.includes(status)) {
    return (
      <div
        className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-[var(--radius)] border border-rose-500/40 bg-rose-500/10 p-4 mb-6"
        data-testid="trial-ended-banner"
      >
        <div className="flex items-start gap-3 flex-1">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-rose-500/15 text-rose-600">
            <AlertTriangle className="h-4.5 w-4.5" />
          </div>
          <div>
            <div className="font-medium text-rose-900" data-testid="trial-ended-title">
              Your free trial has ended
            </div>
            <div className="text-sm text-rose-800/90 mt-0.5">
              We couldn&apos;t process payment for your {planLabel} plan, so tracking is paused. Reactivate in one tap to pick up right where you left off.
            </div>
          </div>
        </div>
        <Button
          onClick={resubscribe}
          disabled={busy}
          className="bg-rose-600 hover:bg-rose-700 text-white shrink-0"
          data-testid="trial-resubscribe-button"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
          Reactivate {planLabel}
        </Button>
      </div>
    );
  }

  // ---- State 2: active free trial → countdown ----
  if (billing.is_trial && billing.trial_end) {
    const left = daysLeft(billing.trial_end);
    const leftLabel = left === 0 ? 'Ends today' : `${left} day${left === 1 ? '' : 's'} left`;
    return (
      <div
        className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-[var(--radius)] border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 p-4 mb-6"
        data-testid="trial-countdown-banner"
      >
        <div className="flex items-start gap-3 flex-1">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]">
            <Gift className="h-4.5 w-4.5" />
          </div>
          <div>
            <div className="font-medium text-foreground" data-testid="trial-countdown-title">
              <span className="text-[hsl(var(--accent))] font-semibold">{leftLabel}</span> in your {planLabel} free trial
            </div>
            <div className="text-sm text-muted-foreground mt-0.5" data-testid="trial-countdown-subtitle">
              Trial ends {String(billing.trial_end).slice(0, 10)}
              {price ? ` — then $${price}/mo` : ''}. Cancel anytime before then to avoid charges.
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/app/settings')}
          className="shrink-0 border-[hsl(var(--accent))]/40"
          data-testid="trial-manage-plan-button"
        >
          Manage plan <ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
      </div>
    );
  }

  return null;
};
